// Match list: every counted match, newest first, narrowed by model, opponent, side, setup
// and mode. The filters live in the URL, so a profile can link to "this model's matches".

import type { ModeFilter, ModelInfo, SetupFilter } from '../src/api/contract';
import type { Side } from '../src/types';
import { api } from './api';
import { liveBanner } from './banner';
import { matchList } from './cards';
import { getParam, h, replaceQuery, requireEl } from './dom';
import { isModeFilter, isSetupFilter } from './format';

interface State {
  model: string | null;
  opponent: string | null;
  role: Side | null;
  setup: SetupFilter;
  mode: ModeFilter;
}

export async function initMatches(): Promise<void> {
  const box = requireEl('match-list');
  const title = requireEl('ml-title');
  const modelSel = requireEl<HTMLSelectElement>('ml-model');
  const oppSel = requireEl<HTMLSelectElement>('ml-opponent');
  const roleSel = requireEl<HTMLSelectElement>('ml-role');
  const setupSel = requireEl<HTMLSelectElement>('ml-setup');
  const modeSel = requireEl<HTMLSelectElement>('ml-mode');
  const clear = requireEl<HTMLButtonElement>('ml-clear');

  const roleParam = getParam('role');
  const setupParam = getParam('setup');
  const modeParam = getParam('mode');
  const state: State = {
    model: getParam('model'),
    opponent: getParam('opponent'),
    role: roleParam === 'attacker' || roleParam === 'defender' ? roleParam : null,
    setup: isSetupFilter(setupParam) ? setupParam : 'all',
    mode: isModeFilter(modeParam) ? modeParam : 'all',
  };
  let names = new Map<string, string>();

  const fillModels = (models: ModelInfo[]): void => {
    const sorted = models.filter((m) => m && typeof m.id === 'string').sort((x, y) => (x.displayName || x.id).localeCompare(y.displayName || y.id));
    names = new Map(sorted.map((m) => [m.id, m.displayName || m.id]));
    for (const [sel, value, any] of [
      [modelSel, state.model, 'Any model'],
      [oppSel, state.opponent, 'Any opponent'],
    ] as const) {
      const opts = [h('option', { attrs: { value: '' } }, any), ...sorted.map((m) => h('option', { attrs: { value: m.id } }, m.displayName || m.id))];
      if (value && !names.has(value)) opts.push(h('option', { attrs: { value } }, value));
      sel.replaceChildren(...opts);
      sel.value = value ?? '';
    }
  };

  const sync = (): void => {
    modelSel.value = state.model ?? '';
    // Opponent and side narrow a chosen model; without one they mean nothing.
    oppSel.disabled = !state.model;
    roleSel.disabled = !state.model;
    oppSel.value = state.model ? (state.opponent ?? '') : '';
    roleSel.value = state.model ? (state.role ?? '') : '';
    setupSel.value = state.setup;
    modeSel.value = state.mode;
    clear.hidden = !state.model && state.setup === 'all' && state.mode === 'all';
    replaceQuery({
      model: state.model,
      opponent: state.model ? state.opponent : null,
      role: state.model ? state.role : null,
      setup: state.setup === 'all' ? null : state.setup,
      mode: state.mode === 'all' ? null : state.mode,
    });
    const name = state.model ? (names.get(state.model) ?? state.model) : null;
    const opp = state.model && state.opponent ? (names.get(state.opponent) ?? state.opponent) : null;
    title.textContent = !name
      ? 'All matches'
      : state.role === 'attacker'
        ? `${name} attacking${opp ? ` ${opp}` : ''}`
        : state.role === 'defender'
          ? `${name} defending${opp ? ` against ${opp}` : ''}`
          : opp
            ? `${name} and ${opp}`
            : `${name}'s matches`;
  };

  const load = (): void => {
    sync();
    // A fresh target per load: a slow, older request lands in a detached box, never over newer results.
    const target = h('div');
    box.replaceChildren(target);
    void matchList(target, {
      load: (before) =>
        api.matches({
          model: state.model ?? undefined,
          opponent: state.model ? (state.opponent ?? undefined) : undefined,
          role: state.model ? (state.role ?? undefined) : undefined,
          setup: state.setup,
          mode: state.mode,
          before,
          limit: 30,
        }),
      emptyTitle: 'No matches found',
      emptyDetail: state.model || state.setup !== 'all' || state.mode !== 'all' ? 'Nothing matches these filters yet. Clear them to see every match.' : 'Finished matches show up here, newest first.',
    });
  };

  modelSel.addEventListener('change', () => {
    state.model = modelSel.value || null;
    if (state.opponent === state.model) state.opponent = null;
    load();
  });
  oppSel.addEventListener('change', () => {
    state.opponent = oppSel.value && oppSel.value !== state.model ? oppSel.value : null;
    load();
  });
  roleSel.addEventListener('change', () => {
    state.role = roleSel.value === 'attacker' || roleSel.value === 'defender' ? roleSel.value : null;
    load();
  });
  setupSel.addEventListener('change', () => {
    state.setup = isSetupFilter(setupSel.value) ? setupSel.value : 'all';
    load();
  });
  modeSel.addEventListener('change', () => {
    state.mode = isModeFilter(modeSel.value) ? modeSel.value : 'all';
    load();
  });
  clear.addEventListener('click', () => {
    Object.assign(state, { model: null, opponent: null, role: null, setup: 'all', mode: 'all' });
    load();
  });

  const models = api
    .models()
    .then((res) => fillModels(Array.isArray(res.models) ? res.models : []))
    .catch(() => {
      modelSel.replaceChildren(h('option', { attrs: { value: '' } }, 'Could not load models'));
    })
    .finally(() => {
      modelSel.disabled = false;
      sync();
    });
  load();
  await Promise.all([models, liveBanner(requireEl('live-banner'))]);
}
